/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, ReactNode, useCallback, useEffect, useState} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {DownloadJobTasks} from '@ion/core/src/types/entities';
import Dropdown, {Options} from '@ion/dls/src/components/menus/Dropdown';

// defines the dependent-task types chooser prop definitions ...
export interface DependentTaskChooserTypes {
  taskIndex: number;
  tasks: DownloadJobTasks;
  dependentTaskOptions?: Options[];
  handleDependentTask: Function;
  isDevicesSelected?: boolean;
  isRequired?: boolean;
}

const useStyles = makeStyles(() => ({
  inputField : {
    height : '1.1876em !important'
  },
  onOptionsText : {
    padding : '0 !important'
  }
}));

/**
 * The DependentTaskChooser provides a list of all the tasks, excluding the current task
 * and allows the user to select a dependent task from the list.
 *
 * ```typescript
 *
 *     import {DependentTaskChooser} from './DependentTaskChooser';
 *
 *     <DependentTaskChooser taskIndex={index}
 *      tasks={tasks}
 *      dependentTaskOptions={dependentTaskOptions}
 *      isDevicesSelected={isDevicesSelected}
 *      handleDependentTask={handleDependentTask} />
 *
 * ```
 */
function DependentTaskChooser({
  taskIndex,
  tasks,
  dependentTaskOptions,
  handleDependentTask,
  isDevicesSelected = false,
  isRequired = false
}: DependentTaskChooserTypes) {
  const classes                           = useStyles();
  const currentTask                       = tasks?.[taskIndex]?.task || {};
  const {dependency: {index = null} = {}} = currentTask?.metaData || {};
  const selectedTask                      = (index ?? 'none') !== 'none' ? `${index + 1}` : '';
  const [taskOptions, setTaskOptions]     = useState([]);

  const isDisable = (
    isUndefined(currentTask?.fileType) ||
    currentTask?.deviceType === '' ||
    currentTask?.fileType === '' ||
    currentTask?.fileName === ''
  );
  
  useEffect(() => {
    setTaskOptions(
      dependentTaskOptions?.filter((_, index) => index < taskIndex) || []
    );
  }, [dependentTaskOptions, taskIndex]);

  function handleChange({target: {value}}) {
    handleDependentTask({
      taskIndex,
      tasks        : {tasks},
      selectedTask : value
    });
  }

  const noOptionsText = useCallback((): ReactNode => {
    return (
      <div className={classes.onOptionsText}>
        {translate('jobcreate:dependentTask.noOptionsText')}
      </div>
    );
  }, [taskOptions]);

  return (
    <Dropdown displaySelectedLabel
      required={isRequired}
      placeholder={translate('jobcreate:fields.dependentTask.placeholder')}
      noOptionsText={noOptionsText()}
      options={taskOptions}
      disabled={!isDevicesSelected || isDisable}
      title={isDevicesSelected && isDisable ? translate('jobcreate:fields.dependentTask.tooltipText') : ''}
      onChange={handleChange}
      externalClasses={{
        input : classes.inputField
      }}
      value={selectedTask} />
  );
}

export default memo(DependentTaskChooser);
